import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Edit2, X, Save, Building2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmpresasPanel({ password, onChange }) {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null); // company | "new"

  const headers = { "X-Admin-Password": password };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/companies`, { headers });
      setCompanies(data.companies || []);
    } catch {
      toast.error("Error cargando empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/admin/companies/${c.id}`, { active: !c.active }, { headers });
      toast.success(!c.active ? "Empresa activada" : "Empresa desactivada");
      load();
      onChange?.();
    } catch {
      toast.error("Error actualizando");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"? Los leads ya etiquetados perderán la asociación.`)) return;
    try {
      await axios.delete(`${API}/admin/companies/${c.id}`, { headers });
      toast.success("Empresa eliminada");
      load();
      onChange?.();
    } catch {
      toast.error("Error eliminando");
    }
  };

  const save = async (payload) => {
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/companies`, payload, { headers });
        toast.success("Empresa creada");
      } else {
        await axios.put(`${API}/admin/companies/${editing.id}`, payload, { headers });
        toast.success("Empresa actualizada");
      }
      setEditing(null);
      load();
      onChange?.();
    } catch (e) {
      const msg = e.response?.data?.detail || "Error guardando";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#29BC9B]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="empresas-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#29BC9B]">Etiquetas</div>
          <h2 className="font-display text-2xl text-white mt-1">Empresas</h2>
          <p className="text-xs text-white/40 mt-1">
            Crea códigos para agrupar participantes por organización.
          </p>
        </div>
        <button
          data-testid="empresa-new-btn"
          onClick={() => setEditing("new")}
          className="btn-teal !py-2 !px-4 !text-[10px]"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva empresa
        </button>
      </div>

      {companies.length === 0 ? (
        <div className="p-10 rounded-sm border border-white/8 bg-[#0A0A0A] text-center" data-testid="empresas-empty">
          <Building2 className="w-6 h-6 text-white/30 mx-auto mb-3" />
          <p className="text-sm text-white/50">Aún no hay empresas. Crea la primera para empezar a etiquetar leads.</p>
        </div>
      ) : (
        <div className="rounded-md border border-white/8 overflow-hidden">
          <table className="w-full text-sm" data-testid="empresas-table">
            <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
              <tr>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Leads</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`empresa-row-${c.code}`}>
                  <td className="px-4 py-3 text-white">{c.name}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded-sm bg-[#29BC9B]/10 text-[#29BC9B] text-xs font-mono tracking-wider">
                      {c.code}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-white/70">{c.leads_count}</td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={c.active}
                      onCheckedChange={() => toggleActive(c)}
                      data-testid={`empresa-toggle-${c.code}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setEditing(c)}
                        data-testid={`empresa-edit-${c.code}`}
                        className="p-2 rounded-sm hover:bg-white/5 text-white/60 hover:text-white"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(c)}
                        data-testid={`empresa-delete-${c.code}`}
                        className="p-2 rounded-sm hover:bg-red-500/10 text-white/40 hover:text-red-400"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CompanyEditor
          company={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function CompanyEditor({ company, onClose, onSave }) {
  const [name, setName] = useState(company?.name || "");
  const [code, setCode] = useState(company?.code || "");
  const [active, setActive] = useState(company ? !!company.active : true);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Nombre obligatorio");
    if (code.trim().length < 2) return toast.error("Código demasiado corto");
    const payload = { name: name.trim(), code: code.trim().toUpperCase() };
    if (company) payload.active = active;
    onSave(payload);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#070707] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {company ? "Editar empresa" : "Nueva empresa"}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-xs">
            Define el nombre legible y el código que los participantes ingresarán.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-4" data-testid="empresa-editor-form">
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.3em] text-white/60">Nombre</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Roiseller"
              data-testid="empresa-input-name"
              className="bg-[#050505] border-white/10 text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.3em] text-white/60">Código</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ROISELLER2026"
              data-testid="empresa-input-code"
              className="bg-[#050505] border-white/10 text-white font-mono tracking-wider"
            />
            <p className="text-[10px] text-white/30">Se convierte a mayúsculas automáticamente.</p>
          </div>
          {company && (
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={active} onCheckedChange={setActive} data-testid="empresa-input-active" />
              <span className="text-sm text-white/70">{active ? "Activa" : "Inactiva"}</span>
            </div>
          )}
          <div className="flex gap-3 justify-end pt-4 border-t border-white/8">
            <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4 !text-[10px]">
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
            <button type="submit" data-testid="empresa-save-btn" className="btn-teal !py-2 !px-4 !text-[10px]">
              <Save className="w-3.5 h-3.5" /> Guardar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
